import Breadcrumbs, { Breadcrumb } from 'components/knowledge-base/breadcrumbs/Breadcrumbs'
import SocialMediaLinks from 'components/knowledge-base/SocialMediaLinks'
import { ContentImage } from 'components/SeoPage'
import StaticPageLayout from 'components/StaticPageLayout'
import AuthorAndPublishedDate from 'components/UI/AuthorAndPublishedDate'
import StyledMarkdown from 'components/UI/StyledMarkdown'
import { graphql, useStaticQuery } from 'gatsby'
import React, { ReactElement } from 'react'
import { Flex, Text } from 'theme-ui'
import getEnglishStrings from 'utils/getEnglishStrings'
import usePlatformTagId from 'utils/usePlatformTagId'
import rehypeRaw from 'rehype-raw'
import useBreakpointIndex from 'theme/useBreakpointIndex'

export interface KnowledgeBaseArticleContext {
  article: Queries.KnowledgeBaseQuery['directus']['knowledge_base_articles'][number]
}

export default function KnowledgeBaseArticle({
  pageContext: { article },
}: {
  pageContext: KnowledgeBaseArticleContext
}): ReactElement {
  const kbArticleQuery = useStaticQuery<Queries.KBArticleQuery>(graphql`
    query KBArticle {
      directus {
        general_translations {
          strings {
            help
            languages_code {
              code
            }
          }
        }
      }
    }
  `)

  const bredcrumbStrings = getEnglishStrings(
    kbArticleQuery?.directus?.general_translations?.strings,
  )

  const articleStrings = getEnglishStrings(article?.strings)
  const body = articleStrings?.body
  const { selectedPlatformTagId } = usePlatformTagId()

  const categoryStrings = getEnglishStrings(article?.category?.strings)
  const breadcrumbs: Breadcrumb[] = [
    {
      title: bredcrumbStrings?.help ?? '',
      href: selectedPlatformTagId
        ? `/knowledge-base?platform=${selectedPlatformTagId}`
        : '/knowledge-base',
    },
    {
      title: categoryStrings?.name ?? '',
      href: `/knowledge-base/categories/${article.category?.slug}`,
    },
  ]

  const breakpointIndex = useBreakpointIndex()
  const isMobile = breakpointIndex === 0

  return (
    <StaticPageLayout
      title={`${articleStrings?.title} | Windscribe`}
      metaDescription={articleStrings?.meta_description}
      metaKeywords={articleStrings?.meta_keywords}
      footerColour="contentAndFooterBackground"
    >
      <Flex sx={{ flex: 0, flexDirection: 'column', alignItems: 'center', gap: '1.6rem' }}>
        <Text
          data-testid="article-title"
          sx={{
            px: ['2.4rem', '4.8rem', '12.6rem'],
            width: '100%',
            textAlign: 'center',
          }}
          variant={isMobile ? 'h1Small' : 'h1Big'}
          data-typesense-field="title"
        >
          {articleStrings?.title}
        </Text>
        <AuthorAndPublishedDate
          author={article.user_created?.first_name + ' ' + article.user_created?.last_name}
          publishedAt={new Date(article.date_updated)}
        />
      </Flex>
      <ContentImage />
      <Flex
        sx={{
          flexDirection: 'column',
          backgroundColor: '#030407',
        }}
      >
        <Flex
          sx={{
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            flex: 1,
            mb: ['4.8rem', 0],
          }}
        >
          <Flex
            sx={{
              flexDirection: 'column',
              py: [0, '5.6rem', '5.6rem'],
              maxWidth: '89rem',
              width: '100%',
              flex: 1,
              alignItems: 'center',
              px: ['1.6rem', '1.6rem', 0],
            }}
          >
            <Breadcrumbs breadcrumbs={breadcrumbs} />

            <div data-typesense-field="body">
              <StyledMarkdown rehypePlugins={[rehypeRaw]}>{body}</StyledMarkdown>
            </div>
          </Flex>
        </Flex>
        <SocialMediaLinks />
      </Flex>
    </StaticPageLayout>
  )
}
